import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Laptop, Truck } from "lucide-react";

import { SiteHeader } from "@/components/site/site-header";
import { SiteFooter } from "@/components/site/site-footer";
import { PublicErrorState } from "@/components/site/public-error";

import { getStoredAthleteRef } from "@/lib/athlete-ref";
import { listPrograms, submitApplication } from "@/lib/public.functions";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { T1D_RELATIONSHIPS } from "@/lib/t1d-relationships";
import { US_STATES } from "@/lib/us-states";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { FieldError, ValidationSummary } from "@/components/forms/field-error";
import { PhoneInput } from "@/components/forms/phone-input";
import { useInlineValidation } from "@/hooks/use-inline-validation";
import { reportHandledSync } from "@/lib/observability";

const programsQuery = queryOptions({
  queryKey: ["programs", "public"],
  queryFn: () => listPrograms(),
});

type Track = "remote" | "onsite" | "both";


const TRACK_OPTIONS: {
  value: "remote" | "onsite";
  label: string;
  icon: typeof Laptop;
  blurb: string;
}[] = [
  {
    value: "remote",
    label: "Remote outreach",
    icon: Laptop,
    blurb: "Add a contact, pick a ready-made message, click send. About an hour a week.",
  },
  {
    value: "onsite",
    label: "On-site crew",
    icon: Truck,
    blurb: "Work events in Northern Kentucky and Greater Cincinnati.",
  },
];


const TITLE = "Apply to Volunteer | T1D Warriors, Burlington KY";
const DESCRIPTION =
  "Apply to volunteer with T1D Warriors, a local 501(c)(3) nonprofit based in Burlington, Kentucky, serving families across Northern Kentucky and Greater Cincinnati. Remote outreach or on-site crew.";

export const Route = createFileRoute("/apply")({
  validateSearch: (search: Record<string, unknown>): { track?: Track; ref?: string } => {
    const raw = String(search.track ?? "");
    const ref = String(search.ref ?? "").trim().toLowerCase();
    return {
      ...(["remote", "onsite", "both"].includes(raw) ? { track: raw as Track } : {}),
      ...(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(ref) ? { ref } : {}),
    };
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(programsQuery),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://volunteer.t1dw.org/apply" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: "https://volunteer.t1dw.org/apply" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "NGO",
          name: "T1D Warriors",
          url: "https://t1dw.org",
          description:
            "T1D Warriors is a local 501(c)(3) nonprofit based in Burlington, Kentucky, serving families across Northern Kentucky and Greater Cincinnati with free Type 1 diabetes programs and the T1D Warriors Scholarship Fund.",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Burlington",
            addressRegion: "KY",
            addressCountry: "US",
          },
        }),
      },
    ],
  }),
  errorComponent: ({ error }) => (
    <PublicErrorState
      error={error}
      route="/apply"
      message="We could not load the application form just now. Refresh and it should come back."
    />
  ),
  component: ApplyPage,

});

function ApplyPage() {
  const { data: programs } = useSuspenseQuery(programsQuery);
  const { track: initialTrack, ref: referralCode } = Route.useSearch();
  const submit = useServerFn(submitApplication);
  const [tracks, setTracks] = useState<("remote" | "onsite")[]>(
    initialTrack === "both" ? ["remote", "onsite"] : [initialTrack ?? "remote"],
  );
  const [selected, setSelected] = useState<string[]>([]);
  const [smsConsent, setSmsConsent] = useState(false);
  const [emailConsent, setEmailConsent] = useState(true);
  const { errors, issues, fieldProps, focusField, resetErrors } = useInlineValidation();
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [done, setDone] = useState(false);

  const track: Track =
    tracks.includes("remote") && tracks.includes("onsite")
      ? "both"
      : tracks.includes("onsite")
        ? "onsite"
        : "remote";

  const toggleTrack = (value: "remote" | "onsite") =>
    setTracks((prev) => (prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]));

  const allSelected = programs.length > 0 && selected.length === programs.length;

  const toggle = (name: string) =>
    setSelected((prev) => (prev.includes(name) ? prev.filter((p) => p !== name) : [...prev, name]));


  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // `submitting` state only disables the button on the next render, so a fast
    // double tap can fire two applications. The ref locks it synchronously.
    if (submittingRef.current) return;
    submittingRef.current = true;
    resetErrors();

    if (selected.length === 0) {
      submittingRef.current = false;
      toast.error("Select at least one program you would like to help with.");
      return;
    }

    const form = new FormData(event.currentTarget);

    setSubmitting(true);
    try {
      await submit({
        data: {
          full_name: [String(form.get("first_name") ?? "").trim(), String(form.get("last_name") ?? "").trim()]
            .filter(Boolean)
            .join(" "),
          email: String(form.get("email") ?? ""),
          phone: String(form.get("phone") ?? ""),
          birthday: String(form.get("birthday") ?? ""),
          city: String(form.get("city") ?? ""),
          state: String(form.get("state") ?? ""),
          why_help: String(form.get("why_help") ?? ""),
          t1d_connection: String(form.get("t1d_connection") ?? ""),
          availability: String(form.get("availability") ?? ""),
          programs_of_interest: selected,
          volunteer_track: track,
          sms_consent: smsConsent,
          sms_consent_source: "public application form",
          email_consent: emailConsent,
          email_consent_source: "public application form",
          referral_code: referralCode,
          athlete_ref: getStoredAthleteRef(),
        },
      });
      setDone(true);
    } catch (error) {
      reportHandledSync(error, { feature: "apply", action: "handleSubmit" });
      toast.error(error instanceof Error ? error.message : "Something went wrong.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }


  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="app-light">
        <main
          className="mx-auto max-w-3xl px-4 pb-20 sm:px-6 sm:pb-24"
          style={{ paddingTop: "calc(var(--site-hdr, 72px) + 2.5rem)" }}
        >
          {done ? (
            <div className="panel p-6 text-center sm:p-10">
              <CheckCircle2 className="mx-auto h-12 w-12 text-cyan" />
              <h1 className="t-h1 mt-6 text-ice">Application received</h1>
              <p className="t-body mx-auto mt-3 max-w-md">
                Someone on our team reads every one of these, usually within a few days. If you are
                a fit, you will get an email invite to set up your volunteer account.
              </p>
              <Link
                to="/"
                className="mt-8 inline-flex rounded-sm border border-border px-6 py-3 text-sm font-bold uppercase tracking-wide text-ice transition-colors hover:border-cyan hover:text-cyan"
              >
                Back to home
              </Link>
            </div>
          ) : (
            <>
              <p className="t-eyebrow">Volunteer application</p>
              <h1 className="t-h1 mt-3 text-balance text-ice sm:mt-4">
                Tell us how you want to help
              </h1>
              <p className="t-body mt-4">
                T1D Warriors is a small Kentucky nonprofit, so a real person reads this, not a form
                robot. Start by picking the kind of volunteering you want to do.
              </p>

              <form noValidate={false}
                onSubmit={handleSubmit}
                className="panel mt-8 space-y-8 p-5 sm:mt-10 sm:space-y-7 sm:p-7">
                <ValidationSummary issues={issues} onJump={focusField} />

                <fieldset className="space-y-3">
                  <legend className="t-meta mb-3">
                    Which track do you want? Pick one or both.
                  </legend>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {TRACK_OPTIONS.map((option) => {
                      const active = tracks.includes(option.value);
                      return (
                        <label
                          key={option.value}
                          htmlFor={`track-${option.value}`}
                          className={`flex min-h-[92px] cursor-pointer gap-3 rounded-sm border p-4 text-left transition-colors ${
                            active
                              ? "border-cyan bg-cyan/10"
                              : "border-border bg-secondary/50 hover:border-cyan/60"
                          }`}
                        >
                          <Checkbox
                            id={`track-${option.value}`}
                            className="mt-0.5"
                            aria-label={option.label}
                            checked={active}
                            onCheckedChange={() => toggleTrack(option.value)}
                          />
                          <span className="min-w-0">
                            <option.icon className="h-5 w-5 text-cyan" />
                            <span className="mt-3 block text-sm font-bold uppercase tracking-wide text-ice">
                              {option.label}
                            </span>
                            <span className="mt-1.5 block text-[0.8125rem] leading-relaxed text-muted-foreground">
                              {option.blurb}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>


                <div className="grid gap-6 sm:gap-5 sm:grid-cols-2">
                  <Field label="First name" name="first_name" required fieldProps={fieldProps} errors={errors} />
                  <Field label="Last name" name="last_name" required fieldProps={fieldProps} errors={errors} />
                  <Field label="Email" name="email" type="email" required fieldProps={fieldProps} errors={errors} />
                  <div data-field-wrapper className="min-w-0 space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <PhoneInput id="phone" required {...fieldProps("phone", "Phone")} />
                    <p className="t-meta">We text shift details, so a mobile number works best.</p>
                    <FieldError id="phone-error" message={errors["phone"]} />
                  </div>
                  <Field label="Birthday" name="birthday" type="date" required fieldProps={fieldProps} errors={errors} />
                  <Field
                    label="Availability"
                    name="availability"
                    required
                    placeholder="A few hours a week, weekend mornings, etc."
                    fieldProps={fieldProps}
                    errors={errors}
                  />
                  <Field label="City" name="city" required fieldProps={fieldProps} errors={errors} />
                  <div data-field-wrapper className="min-w-0 space-y-2">
                    <Label htmlFor="state">State</Label>
                    <select
                      id="state"
                      required
                      defaultValue=""
                      {...fieldProps("state", "State")}
                      className="h-11 w-full rounded-sm border border-input bg-background px-3 text-[0.9375rem]"
                    >
                      {US_STATES.map((state) => (
                        <option key={state.value} value={state.value} disabled={state.value === ""}>
                          {state.label}
                        </option>
                      ))}
                    </select>
                    <FieldError id="state-error" message={errors.state} />
                  </div>
                </div>
                <p className="t-meta">
                  Heads up: event announcements and new volunteer opportunities only go to people
                  who turn on email or text below. Without one of these on, you will only hear from
                  us about shifts you have already claimed.
                </p>

                <div className="rounded-[4px] border border-border bg-muted/40 p-4">

                  <label htmlFor="sms_consent" className="flex cursor-pointer items-start gap-3">
                    <Checkbox
                      id="sms_consent"
                      name="sms_consent"
                      checked={smsConsent}
                      onCheckedChange={(value) => setSmsConsent(value === true)}
                      className="mt-0.5"
                    />
                    <span className="space-y-1">
                      <span className="block text-sm font-medium">
                        Yes, text me about shifts and volunteer updates
                      </span>
                      <span className="t-meta block">
                        By checking this box you agree to receive text messages from T1D Warriors at
                        the mobile number above, including shift reminders and volunteer updates.
                        Message and data rates may apply, message frequency varies, and you can
                        reply STOP at any time to opt out or HELP for help. Consent is not a
                        condition of volunteering.
                      </span>
                    </span>
                  </label>
                </div>
                <div className="rounded-[4px] border border-border bg-muted/40 p-4">
                  <label htmlFor="email_consent" className="flex cursor-pointer items-start gap-3">
                    <Checkbox
                      id="email_consent"
                      name="email_consent"
                      checked={emailConsent}
                      onCheckedChange={(value) => setEmailConsent(value === true)}
                      className="mt-0.5"
                    />
                    <span className="space-y-1">
                      <span className="block text-sm font-medium">
                        Yes, email me about volunteer opportunities and updates
                      </span>
                      <span className="t-meta block">
                        By checking this box you agree to receive emails from T1D Warriors at the
                        address above, including shift details, event invitations, and volunteer
                        news. You can unsubscribe at any time using the link in any email. Consent
                        is not a condition of volunteering, and we will still email you the
                        essentials about shifts you sign up for.
                      </span>
                    </span>
                  </label>
                </div>



                <p className="t-meta">
                  We ask for city and state on every application so we can match you with the right
                  opportunities, whether you work remotely or on-site.
                </p>

                <div data-field-wrapper className="space-y-2">
                  <Label htmlFor="t1d_connection">
                    What is your primary relationship to Type 1 Diabetes?
                    <span aria-hidden="true"> *</span>
                  </Label>
                  <select
                    id="t1d_connection"
                    required
                    defaultValue=""
                    {...fieldProps("t1d_connection", "What is your primary relationship to Type 1 Diabetes?")}
                    className="h-11 w-full rounded-sm border border-input bg-background px-3 text-[0.9375rem]"
                  >
                    <option value="" disabled>
                      Select one
                    </option>
                    {T1D_RELATIONSHIPS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <FieldError id="t1d_connection-error" message={errors.t1d_connection} />
                </div>

                <div data-field-wrapper className="space-y-2">
                  <Label htmlFor="why_help">Why do you want to help?</Label>
                  <Textarea
                    id="why_help"
                    rows={4}
                    required
                    maxLength={2000}
                    {...fieldProps("why_help", "Why do you want to help?")}
                  />
                  <FieldError id="why_help-error" message={errors.why_help} />
                </div>

                <div className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label>
                      {track === "onsite"
                        ? "Programs you would like to work"
                        : "Programs you would like to pitch"}
                      <span aria-hidden="true"> *</span>
                    </Label>
                    <button
                      type="button"
                      onClick={() =>
                        setSelected(allSelected ? [] : programs.map((program) => program.name))
                      }
                      className="text-[0.8125rem] font-semibold text-cyan underline-offset-4 hover:underline"
                    >
                      {allSelected ? "Deselect all" : "Select all"}
                    </button>
                  </div>

                  <div className="grid gap-3.5 sm:grid-cols-2">
                    {programs.map((program) => (
                      <label
                        key={program.id}
                        htmlFor={`program-${program.id}`}
                        className="flex min-h-[52px] cursor-pointer items-center gap-3 rounded-sm border border-border bg-secondary/50 px-4 py-3.5 text-[0.9375rem] text-foreground"
                      >
                        <Checkbox
                          id={`program-${program.id}`}
                          aria-label={program.name}
                          checked={selected.includes(program.name)}
                          onCheckedChange={() => toggle(program.name)}
                        />
                        <span className="min-w-0">{program.name}</span>
                      </label>
                    ))}
                  </div>
                  {selected.length === 0 && (
                    <p className="t-meta text-destructive">Select at least one program.</p>
                  )}
                </div>



                <Button type="submit" disabled={submitting} className="w-full uppercase">
                  {submitting ? "Sending..." : "Submit application"}
                </Button>
              </form>
            </>
          )}
        </main>
      </div>
      <SiteFooter />
    </div>
  );
}


function Field({
  label,
  name,
  type = "text",
  required,
  placeholder,
  fieldProps,
  errors,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  fieldProps: (name: string, label?: string) => Record<string, unknown>;
  errors: Record<string, string>;
}) {
  return (
    <div data-field-wrapper className="min-w-0 space-y-2">
      <Label htmlFor={name}>{label}</Label>
      <Input
        id={name}
        type={type}
        required={required}
        placeholder={placeholder}
        maxLength={255}
        {...fieldProps(name, label)}
      />
      <FieldError id={`${name}-error`} message={errors[name]} />
    </div>
  );
}
